import * as React from 'react';
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InfoEntry } from './InfoEntry';
import { getLatestUndistortedAIEyeImage } from '../apis/calyxSaaSDashboardAPI';
import { formatISODate } from '../utils/format';

/**
 * @param {object} props
 * @param {boolean} props.visible
 * @param {object} props.targetScale
 * @param {boolean} props.autoRefreshMode
 * @param {object} props.latestAIEyeStatusData
 * @param {() => void} props.onHide
 */
export const ScaleLatestImageDialog = (props) => {
  const { targetScale, latestAIEyeStatusData } = props;

  const latestAIEyeStatusDataTime = latestAIEyeStatusData != null ? new Date(latestAIEyeStatusData.messageAt) : null;
  const { cameraPosition } = latestAIEyeStatusData ?? {};

  const [scaleLatestImageUrl, setScaleLatestImageUrl] = React.useState(null);
  const [scaleLatestImageTimestamp, setScaleLatestImageTimestamp] = React.useState(null);
  const [scaleLatestImageError, setScaleLatestImageError] = React.useState(null);
  const [scaleLatestImageLoading, setScaleLatestImageLoading] = React.useState(false);

  const refetchImage = React.useCallback(async () => {
    setScaleLatestImageError(null);
    setScaleLatestImageLoading(true);

    try {
      const { blob: imageBlob, timestamp: imageTimestamp } = await getLatestUndistortedAIEyeImage({
        houseId: targetScale.houseId ?? '000000000000000000000000',
        serialNumber: targetScale.serialNumber,
      });
      const imageBlobUrl = URL.createObjectURL(imageBlob);

      setScaleLatestImageUrl(imageBlobUrl);
      setScaleLatestImageTimestamp(imageTimestamp);
    } catch (err) {
      setScaleLatestImageError(err);
    }

    setScaleLatestImageLoading(false);
  }, [targetScale.houseId, targetScale.serialNumber]);

  React.useEffect(() => {
    if (!props.visible) return;

    refetchImage();
  }, [props.visible, refetchImage])

  React.useEffect(() => {
    if (!props.autoRefreshMode || !props.visible) return;

    const intervalId = window.setInterval(() => {
      refetchImage();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [props.autoRefreshMode, props.visible, refetchImage]);

  return (
    <Dialog
      visible={props.visible}
      header={`Latest Undistorted Image of ${targetScale.serialNumber}`}
      footer={<div />}
      style={{ width: '800px' }}
      blockScroll
      dismissableMask
      onHide={props.onHide}
    >
      <div className="flex flex-column gap-2 mb-2">
        <InfoEntry
          severity={latestAIEyeStatusData != null ? 'info' : 'secondary'}
          icon="pi pi-clock"
        >
          Latest Regular Report: {
            latestAIEyeStatusData != null
              ? formatISODate(latestAIEyeStatusDataTime)
              : 'No Data'
          }
        </InfoEntry>
        <div className="flex flex-column gap-2 pl-4">
          <InfoEntry
            severity={cameraPosition != null ? 'info' : 'secondary'}
            icon="pi pi-camera"
          >
            Camera Position: {
              cameraPosition != null
                ? `(height = ${(cameraPosition.height / 10).toFixed(2)}cm, tilt = (x = ${cameraPosition.tiltAngle.x}°, y = ${cameraPosition.tiltAngle.y}°))`
                : 'No Data'
            }
          </InfoEntry>
        </div>
      </div>

      <div
        className="bg-gray-100"
        style={{ aspectRatio: '16 / 9' }}
      >
        {scaleLatestImageUrl != null ? (
          <img
            src={scaleLatestImageUrl}
            className="block w-full h-full"
            style={{ objectFit: 'contain' }}
            alt=""
          />
        ) : null}
      </div>

      {
        scaleLatestImageLoading ? (
          <p className="text-gray-500 pt-1">
            Loading ...
          </p>
        ) :
        scaleLatestImageError != null ? (
          <p className="text-red-500 pt-1">
            {scaleLatestImageError.toString()}
          </p>
        ) :
        scaleLatestImageUrl != null ? (
          <p className="text-blue-500 pt-1">
            {
              scaleLatestImageTimestamp != null
                ? formatISODate(scaleLatestImageTimestamp)
                : 'N/A'
            }
          </p>
        ) :
        null
      }

      <div className="flex flex-column align-items-center gap-2 mt-4">
        <Button
          severity="help"
          label={props.autoRefreshMode ? 'Auto-Reloading Image' : 'Reload Image'}
          loading={props.autoRefreshMode}
          disabled={props.autoRefreshMode}
          onClick={refetchImage}
        />
      </div>
    </Dialog>
  )
}
